/**
 * Ledger dates.
 *
 * The bank, the scraper, and both apps reckon days in Eastern time. Reading a
 * date off a UTC clock dates anything done after 8pm as tomorrow, which is how
 * a Friday-evening coffee ends up in Saturday's paycheck period.
 *
 * A ledger date is a `YYYY-MM-DD` string, never a timestamp, so comparing two
 * of them is plain string comparison.
 */

export const LEDGER_TIME_ZONE = 'America/New_York';

export interface YearMonthDay {
  year: number;
  month: number;
  day: number;
}

const ledgerParts = new Intl.DateTimeFormat('en-US', {
  timeZone: LEDGER_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * `Intl` is the only way to read calendar fields in another zone without
 * shipping a date library. `formatToParts` rather than `format` because the
 * assembled string differs by locale and ICU version.
 */
export function partsInLedgerZone(date: Date): YearMonthDay {
  const parts = ledgerParts.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((part) => part.type === type);
    return found ? Number(found.value) : 0;
  };
  return { year: get('year'), month: get('month'), day: get('day') };
}

export function ymd(year: number, month: number, day: number): string {
  const pad = (value: number, width: number): string => String(value).padStart(width, '0');
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

/** Today, as the ledger reckons it. */
export function ledgerToday(now: Date = new Date()): string {
  const { year, month, day } = partsInLedgerZone(now);
  return ymd(year, month, day);
}

export function ledgerYear(now: Date = new Date()): number {
  return partsInLedgerZone(now).year;
}

export function lastDayOfMonth(year: number, month: number): number {
  // Day 0 of the next month is the last day of this one.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Splits a `YYYY-MM-DD` string. Returns `null` for anything else. */
export function parseLedgerDate(value: string): YearMonthDay | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** Whole days from one ledger date to another. Negative if `to` is earlier. */
export function daysBetweenLedgerDates(from: string, to: string): number {
  const a = parseLedgerDate(from);
  const b = parseLedgerDate(to);
  if (!a || !b) return 0;
  const start = Date.UTC(a.year, a.month - 1, a.day);
  const end = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((end - start) / 86_400_000);
}

/** Adds days to a ledger date, rolling months and years correctly. */
export function addLedgerDays(date: string, days: number): string {
  const parts = parseLedgerDate(date);
  if (!parts) return date;
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return ymd(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}
